/*
 * kuvr-dpo-retry.js — retry the DPO add-to-cart through Shopify's variant-index race.
 * Added 2026-08-23 (BUG-007). Pairs with the Nexus variant-heal webhook.
 *
 * THE FAULT (Shopify-confirmed platform behaviour; see BUG-007 in .planning/):
 * Shopify registers a new variant with the CART via a background job. DPO mints a
 * variant and adds it in one motion, so the first add races that job; when Shopify's
 * queue lags, the add gets 422 {"message":"Cart Error","description":"Cannot find
 * variant"} even though the variant is real. The Nexus webhook heals the variant
 * within ~10s — this file makes the customer's ORIGINAL click survive those seconds.
 *
 * WHY THIS HOOKS dpoOptions AND NOT XMLHttpRequest:
 * A previous splint (kuvr-variant-touch.js, deleted) wrapped XHR/fetch and missed
 * adds — DPO has several transport paths (jQuery ajax, an iframe GET, a raw form
 * POST). This store runs directAddToCartSubmit=3 → dpoOptions.hybridAddToCart, a
 * named method on a global object, called recursively BY NAME for its own retries —
 * so wrapping the method catches every attempt, whatever the transport underneath.
 *
 * THE ABSURDITY THIS WORKS AROUND:
 * DPO's hybridAddToCart/asyncAddToCart already contain a 1s-interval retry loop
 * (up to 25 attempts!) for failed adds — but the error handler bails out and
 * alert()s the moment responseText contains "Cart Error". "Cannot find variant"
 * carries message:"Cart Error", so the ONE failure their retry loop could ride out
 * is the one they refuse to retry. We cannot edit their hosted options.js
 * (node1.itoris.com), so:
 *   1. every add attempt is recorded (method name, formData, vId, attempt no.)
 *   2. window.alert is filtered: an alert containing "Cannot find variant" within
 *      90s of a recorded add is swallowed, the Nexus touch endpoint is nudged, and
 *      the SAME method is re-invoked with the same formData every 1.5s, up to 14
 *      attempts (~20s — comfortably past the observed ~10s heal time).
 *   3. anything else alerts normally. If all retries drain, the alert shows —
 *      never silently strand a customer.
 *
 * The button's .submitting state is re-applied during retries because DPO's error
 * cleanup (which runs before the alert) removes it, and an idle-looking button
 * invites a second click mid-retry.
 *
 * 🛑 An Itoris update could rename these methods: the wrapper degrades to DPO's
 * stock behaviour (the alert shows) — it cannot make things worse than no file.
 * 🛑 Remove this file, its theme.liquid tag, and the variant-heal webhook together
 * if Shopify ever fixes the race. Registered in kuvr_theme_deploy.py MARKERS.
 */
(function () {
  var MAX_ATTEMPTS = 16;  // total tries incl. the customer's own first one
  var RETRY_FIRST_MS = 400;  // fail fast on the first re-try — the heal may already have landed
  var RETRY_MS = 1500;       // then DPO's own cadence; ~20s total budget
  var STALE_MS = 90000;   // ignore adds older than this when filtering alerts
  var METHODS = ['hybridAddToCart', 'asyncAddToCart'];

  function productId() {
    try {
      if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta &&
          window.ShopifyAnalytics.meta.product && window.ShopifyAnalytics.meta.product.id) {
        return String(window.ShopifyAnalytics.meta.product.id);
      }
    } catch (e) { /* fall through */ }
    return null;
  }

  /** Fire-and-forget: ask Nexus to write to the variant, re-queueing Shopify's
   *  registration job. The webhook does this too; this just shaves seconds. */
  function nudgeHeal(vId) {
    try {
      var pid = productId();
      if (!pid || !vId) return;
      fetch('https://nexus.kuvr.co/api/shopify/variant-touch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ variantId: String(vId), productId: pid }),
        keepalive: true
      }).catch(function () {});
    } catch (e) { /* never break the retry */ }
  }

  var state = { last: null, held: false };

  /* 🛑 The page renders TWO copies of the add-to-cart button (#product-add-to-cart
   * twice — main form + duplicate), and dpoOptions.addToCartButton points at only
   * ONE of them (measured live 2026-08-23: the SECOND). The customer clicks either.
   * Holding just DPO's reference left the visible button idle mid-retry, so Duncan
   * clicked again — a double-add. Hold EVERY candidate, with inline visual state
   * (opacity + "Adding to cart…" text) so it reads as busy even where the theme has
   * no .submitting styling. DPO's error cleanup strips .submitting BEFORE it
   * alerts, so on every consumed alert the busy state must be put back. */
  function busyButtons() {
    var out = [];
    try {
      var seen = [];
      var add = function (el) { if (el && el.nodeType === 1 && seen.indexOf(el) === -1) { seen.push(el); out.push(el); } };
      if (window.dpoOptions) add(window.dpoOptions.addToCartButton);
      var els = document.querySelectorAll('#product-add-to-cart, form[action*="/cart/add"] [type="submit"], form[action*="/cart/add"] button[name="add"]');
      for (var i = 0; i < els.length; i++) add(els[i]);
    } catch (e) { /* cosmetic only */ }
    return out;
  }
  function holdButton() {
    try {
      busyButtons().forEach(function (b) {
        if (!b.__kuvrHeld) {
          b.__kuvrHeld = { html: b.innerHTML, opacity: b.style.opacity };
          b.innerHTML = '<span>Adding to cart…</span>';
        }
        b.disabled = true;
        b.style.opacity = '0.6';
        b.setAttribute('aria-busy', 'true');
        if (b.className.indexOf('submitting') === -1) b.className += ' submitting';
      });
      state.held = true;
    } catch (e) { /* cosmetic only */ }
  }
  function releaseButton() {
    if (!state.held) return;
    try {
      busyButtons().forEach(function (b) {
        if (b.__kuvrHeld) {
          b.innerHTML = b.__kuvrHeld.html;
          b.style.opacity = b.__kuvrHeld.opacity || '';
          b.__kuvrHeld = null;
        }
        b.disabled = false;
        b.removeAttribute('aria-busy');
        b.className = b.className.replace(/\s*\bsubmitting\b/, '');
      });
    } catch (e) { /* cosmetic only */ }
    state.held = false;
  }

  // Exposed for testing and diagnostics: returns true if the alert was consumed
  // and a retry scheduled, false if the alert should surface normally.
  function handleAlert(msg) {
    try {
      var last = state.last;
      if (!last) return false;
      if (String(msg).indexOf('Cannot find variant') === -1) { releaseButton(); return false; }
      if (Date.now() - last.at > STALE_MS) { releaseButton(); return false; }
      if (last.n >= MAX_ATTEMPTS) { releaseButton(); return false; } // give up: alert shows, button usable again
      nudgeHeal(last.vId);
      holdButton();
      setTimeout(function () {
        try {
          window.dpoOptions[last.name](last.formData, last.vId, (last.n || 1) + 1);
        } catch (e) { releaseButton(); /* if DPO is gone, there is nothing left to retry */ }
      }, last.n <= 1 ? RETRY_FIRST_MS : RETRY_MS);
      return true;
    } catch (e) {
      releaseButton();
      return false; // on any doubt, let the alert through
    }
  }

  function wrapMethods() {
    if (!window.dpoOptions) return false;
    var wrappedAny = false;
    METHODS.forEach(function (name) {
      var orig = window.dpoOptions[name];
      if (typeof orig !== 'function' || orig.__kuvrWrapped) { wrappedAny = wrappedAny || !!(orig && orig.__kuvrWrapped); return; }
      var wrapped = function (formData, vId, n) {
        state.last = { name: name, formData: formData, vId: vId, n: n || 1, at: Date.now() };
        return orig.apply(this, arguments);
      };
      wrapped.__kuvrWrapped = true;
      window.dpoOptions[name] = wrapped;
      wrappedAny = true;
    });
    return wrappedAny;
  }

  var realAlert = window.alert;
  window.alert = function (msg) {
    if (handleAlert(msg)) return;
    return realAlert.apply(window, arguments);
  };

  window.__kuvrDpoRetry = { handleAlert: handleAlert, state: state, version: 1 };

  /* WATCHDOG — no add may hang the button forever (added after 2026-08-23 evening:
   * a rate-limited storefront answers /cart/add.js with an HTML 429 page; DPO's
   * error handler does JSON.parse(responseText), THROWS, and never cleans up —
   * the button reads "Adding to cart…" until the customer gives up). If an add
   * was recorded >45s ago and a button still shows busy with no navigation
   * having happened, strip every busy state and tell the customer to retry. */
  setInterval(function () {
    try {
      var last = state.last;
      if (!last || Date.now() - last.at < 45000) return;
      var stuck = false;
      busyButtons().forEach(function (b) {
        if (b.disabled || b.className.indexOf('submitting') > -1 || b.__kuvrHeld) stuck = true;
      });
      if (!stuck && !document.querySelector('.submitting')) return;
      state.last = null;
      releaseButton();
      try {
        if (window.jQuery) window.jQuery('.submitting').removeClass('submitting');
        document.querySelectorAll('.submitting').forEach(function (el) {
          el.className = el.className.replace(/\s*\bsubmitting\b/, '');
        });
      } catch (e) { /* cosmetic */ }
      if (!document.getElementById('kuvr-cart-busy-note')) {
        var note = document.createElement('div');
        note.id = 'kuvr-cart-busy-note';
        note.setAttribute('role', 'alert');
        note.style.cssText = 'margin:10px 0;padding:10px 14px;border:1px solid #c9a227;background:#fdf6e3;color:#5b4a00;font-size:14px;border-radius:4px;';
        note.textContent = 'The store is busy right now — your item was not added. Please try again in a minute.';
        var anchor = busyButtons()[0];
        if (anchor && anchor.parentNode) anchor.parentNode.insertBefore(note, anchor.nextSibling);
        setTimeout(function () { try { note.remove(); } catch (e) {} }, 20000);
      }
    } catch (e) { /* the watchdog must never throw */ }
  }, 5000);

  // dpoOptions arrives whenever Itoris's hosted include finishes; poll for it.
  if (!wrapMethods()) {
    var tries = 0;
    var iv = setInterval(function () {
      if (wrapMethods() || ++tries >= 120) clearInterval(iv); // up to 30s
    }, 250);
  }
})();
